import type { EngineContext, TransmitterData } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import {
  resolveIrsIntakeTaxYear,
  schemaVersionForTaxYear,
  softwareIdForTaxYear,
} from "../../constants/taxYear";
import {
  generateUniqueTransmissionId,
  generateUniqueSubmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { Record1098 } from "./interface/1098-interface";
import { borrowerRecipientDetail, form1098Boxes, form1098Totals } from "./detail";

/**
 * The IRTransmission envelope for one Form 1098 submission. Shared with the
 * correction generator, which passes TransmissionTypeCd "C".
 *
 * Ported from core-api efile/A2A-iris/1098/gen-xml.ts (TS-8587). The transmitter, the TCC
 * and the Software ID come from the EngineContext instead of a database read and the
 * environment; the document is otherwise the same.
 */
export function build1098Transmission(
  ctx: EngineContext,
  details: object[],
  records: Record1098[],
  taxYear: string | number,
  transmitterData: TransmitterData,
  transmissionTypeCd: "O" | "C",
): object {
  return {
    "?xml": { "@_version": "1.0", "@_encoding": "UTF-8" },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation": "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: generateUniqueTransmissionId(ctx.irs.tcc),
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: transmissionTypeCd,
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: "1",
        TotalRecipientFormCnt: details.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: generateUniqueSubmissionId(),
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1098",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: new Date().toISOString().split("T")[0],
              JuratPersonTitleTxt: "CFO",
              PersonNm: transmitterData.contactName || "Payer",
            },
            TotalReportedRcpntFormCnt: details.length.toString(),
            IRSubmission1FormTotals: {
              Form1098TotalAmtGrp: form1098Totals(records),
            },
          },
          IRSubmission1Detail: {
            Form1098Detail: details,
          },
        },
      ],
    },
  };
}

export const xmlBuilder = () =>
  new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: false,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

/**
 * Form 1098 (Mortgage Interest Statement) original transmission -- TS-8587.
 * Records are canonical 1098 records; see ./detail.ts for the column mapping.
 */
export async function generate1098Xml(ctx: EngineContext, records: Record1098[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    return {
      validationResponse: [
        { error: true, message: "No records found or records input is invalid." },
      ],
      xml: null,
      taxYear: null,
    };
  }

  const transmitterData = ctx.transmitter;
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);
  const testFile = transmitterData.testFileIndicator === "T";

  const details = records.map((record) => ({
    "@_xmlns": "urn:us:gov:treasury:irs:ir",
    "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
    TaxYr: taxYear.toString(),
    RecordId: record.id.toString(),
    VoidInd: "0",
    CorrectedInd: record.corrected ? "1" : "0",
    RecipientDetail: borrowerRecipientDetail(record, testFile, ctx.schemaRoot),
    ...(record.BORROWER_ACCOUNT_NUMBER && {
      RecipientAccountNum: String(record.BORROWER_ACCOUNT_NUMBER).trim().slice(0, 30),
    }),
    ...form1098Boxes(record),
  }));

  try {
    const xml = xmlBuilder().build(
      build1098Transmission(ctx, details, records, taxYear, transmitterData, "O"),
    );
    return { xml, taxYear, validationResponse: [] };
  } catch (error) {
    console.error("Error generating 1098 XML:", error);
    return {
      validationResponse: [
        {
          error: true,
          message: "Failed to generate XML",
          details: error instanceof Error ? error.message : String(error),
        },
      ],
      xml: null,
      taxYear,
    };
  }
}
